import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getRightPanelContentById, getRightPanelTitle } from '../../redux/ContentRegistry/rightPanel';
import { closeExpandableColumn } from '../../redux/Slices/expandableColumnSlice';
import './ExpandableColumn.scss';
import Close from '../icons/Close';

const ExpandableColumn = () => {
    const dispatch = useDispatch();
    const { isVisible, contentId, props } = useSelector((state) => state.expandableColumn);
    const [width, setWidth] = useState(() => {
        const savedWidth = localStorage.getItem('expandableColumnWidth');
        return savedWidth ? parseInt(savedWidth, 10) : 290;
    });
    const [isResizing, setIsResizing] = useState(false);
    const resizeRef = useRef(null);

    // Listen for storage changes to sync width across components
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'expandableColumnWidth' && e.newValue) {
                setWidth(parseInt(e.newValue, 10));
            }
        };

        const handleCustomWidthChange = (e) => {
            setWidth(e.detail.width);
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('expandableColumnWidthChange', handleCustomWidthChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('expandableColumnWidthChange', handleCustomWidthChange);
        };
    }, []);

    const handleClose = () => {
        dispatch(closeExpandableColumn());
        window.dispatchEvent(new CustomEvent('collapseSidebar'));
    };

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isVisible) {
                dispatch(closeExpandableColumn());
            }
        };

        if (isVisible) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isVisible, dispatch]);

    // Hide/show zoobbe-sidebar-container based on expandable column visibility
    useEffect(() => {
        const hideSidebarContainers = () => {
            const sidebarContainers = document.querySelectorAll('.zoobbe-sidebar-container');
            sidebarContainers.forEach(container => {
                if (container) {
                    container.style.display = 'none';
                    container.setAttribute('data-hidden-by-expandable', 'true');
                }
            });
        };

        const showSidebarContainers = () => {
            const sidebarContainers = document.querySelectorAll('.zoobbe-sidebar-container');
            sidebarContainers.forEach(container => {
                if (container) {
                    container.style.display = '';
                    container.removeAttribute('data-hidden-by-expandable');
                }
            });
        };

        if (isVisible) {
            // Add class to body for CSS-based hiding
            document.body.classList.add('expandable-column-open');

            // Hide immediately
            hideSidebarContainers();

            // Set up a periodic check to ensure sidebar stays hidden
            // This handles cases where the sidebar might be dynamically added or shown
            const intervalId = setInterval(() => {
                hideSidebarContainers();
            }, 100);

            // Also listen for DOM changes to catch dynamically added sidebars
            const observer = new MutationObserver(() => {
                hideSidebarContainers();
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class']
            });

            return () => {
                clearInterval(intervalId);
                observer.disconnect();
                document.body.classList.remove('expandable-column-open');
                showSidebarContainers();
            };
        } else {
            document.body.classList.remove('expandable-column-open');
            showSidebarContainers();
        }
    }, [isVisible]);

    // Handle mouse down on resize handle
    const handleMouseDown = (e) => {
        setIsResizing(true);
        e.preventDefault();
    };

    // Handle mouse up for resizing
    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Save the width to localStorage
        localStorage.setItem('expandableColumnWidth', width.toString());

        // Dispatch custom event for same-tab synchronization
        window.dispatchEvent(new CustomEvent('expandableColumnWidthChange', {
            detail: { width }
        }));
    }, [width, isResizing]);

    // Handle mouse move for resizing
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;

            const newWidth = e.clientX;
            const minWidth = 250;
            const maxWidth = Math.min(600, window.innerWidth * 0.4); // Max 40% of screen width or 600px

            if (newWidth >= minWidth && newWidth <= maxWidth) {
                setWidth(newWidth);
            }
        };

        if (isResizing) {
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleMouseUp]);

    if (!isVisible || !contentId) {
        return null;
    }

    const content = getRightPanelContentById(contentId, props);
    const title = getRightPanelTitle(contentId);

    return (
        <div
            className={`expandable-column${isResizing ? ' resizing' : ''}`}
            style={{ width: `${width}px`, minWidth: `${width}px` }}
        >
            <div className="expandable-column__header">
                <div className="expandable-column__title-section">
                    <h3 className="expandable-column__title">{title}</h3>
                    
                </div>
                <button
                    className="expandable-column__close-btn"
                    onClick={handleClose}
                    aria-label="Close panel"
                >
                    <Close size={24} />
                </button>
            </div>

            <div className="expandable-column__content">
                {content}
            </div>

            {/* Resize handle */}
            <div
                className={`expandable-column__resize-handle ${isResizing ? 'resizing' : ''}`}
                onMouseDown={handleMouseDown}
                ref={resizeRef}
            />
        </div>
    );
};

export default ExpandableColumn;
